import ResponseHelper from './response-helper';
import Formatter from '../formats';

/**
 * Lib that manages communication related to sign up with Vizzto server.
 * Returns with `code = 0` are considered sucessfull.
 */

/**
 * @typedef FieldErrorResponse All methods can return a failed promise with field validation errors following this structure.
 * @type {Object}
 * @property {Number} code Field validation error code (400)
 * @property {String} message Global error message (like: "Some fields are invalid.").
 * @property {Object} data Holds the errors. Each property is related to a field error. Property's key must be the field name. Property's value must be the error message.
 * @example { code: 400, message: "Request is invalid.", data: { user: "User cannot have more than 3 chars.", password: "Password doesn't have special char." } }
 */

/**
 * @typedef AddressForm
 * @type {Object}
 * @property {String} zip Zip code digits (00000000)
 * @property {String} street
 * @property {String} [number]
 * @property {String} [extra] Address complement
 * @property {String} [district]
 * @property {String} city
 * @property {String} state 2-chars state identifier
 * @property {String} country 2-chars country identifier
 */

/**
 * @typedef SignUpForm
 * @type {Object}
 * @property {String} name
 * @property {String} document CPF
 * @property {String} birthday Birthday digits (DDMMYYYY) — form field name in UI
 * @property {String} nationality
 * @property {String} phone Phone digits (00000000000)
 * @property {AddressForm} address
 * @property {Object} payment
 * @property {String} payment.type Payment type, currently only 'credit-card'
 * @property {Object} payment.creditCard
 * @property {Object} payment.creditCard.owner
 * @property {Boolean} payment.creditCard.owner.same Whether the owner of credit card and user are the same
 * @property {String} [payment.creditCard.owner.document] CPF
 * @property {AddressForm} [payment.creditCard.owner.address]
 * @property {String} payment.creditCard.number Credit card number (digits only)
 * @property {String} payment.creditCard.name Name on card
 * @property {String} payment.creditCard.expiration Expiration date digits (MMYY)
 * @property {String} payment.creditCard.cvv Credit card CVV digits
 */

export default (axios) => ({
  /**
   * Starts the sign up process. Send e-mail and password filled by user.
   * The e-mail must contain an activation link to /sign-up/email-confirm/:hash .
   * @see {@link https://vspuat30-gateway.vizzto.net/swagger-ui.html#/user-viewer-resource}
   * @param {number} distributorId Distributor id
   * @param {String} email User username
   * @param {String} password User password
   * @returns {Promise} Resolved promise if request was sucessfull, rejected promise with error message otherwise
   */
  beginSignUpWithEmail(distributorId, email, password) {
    return axios({
      url: `users/viewer`,
      method: 'post',
      data: {
        email,
        password,
        distributorId
      }
    })
      .then(function () {
        return Promise.resolve({
          data: {}, // return status 201 to confirm the request
          code: 0,
          message: 'CREATED'
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  /**
   * Gets config for sign up process. It should answer the following questions:
   * - Should user choose a plan when signing up? If so, which plans are available and what is the info of each one (title, price, features...)?
   * - Should user complete its profile (i.e: inform his data like name, phone...)
   * - If user must complete its profile, which data should he inform? It should inform a list of the following identifiers: 'personal_data', 'contact_data', 'payment_data'.
   * - Which payment methods we're going to accept? It should inform a list of the following identifiers: 'bank_transfer', 'credit_card', 'paypal'.
   * @see {@link https://swagger.io/}
   * @param {String} utoken Device token (normally stored in localStorage)
   * @returns {Promise} Resolved promise with config data or rejected promise on error.
   */
  getSignUpConfig(utoken) {
    return axios({
      url: `/signups/management`,
      method: 'get',
      headers: {
        utoken
      }
    })
      .then(function (response) {
        return Promise.resolve({
          data: {
            completeProfile: {
              active: response.data.showSignup,
              requires: response.data.signupTypes
            },
            paymentMethods: ['credit_card'] // existe somente este meio no momento
          },
          code: 0,
          message: 'OK'
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  /**
   * Submits the data informed by user on finishing the sign up.
   * Server will return field validation errors.
   * @see {@link https://vspuat30-gateway.vizzto.net/swagger-ui.html#!/user-viewer-resource/completeUserCreationUsingPUT}
   * @param {number} distributorId Distributor id
   * @param {number} planId Plan id
   * @param {string} uname User name
   * @param {string} gatewayPublicKey Key gatewayPublicKey
   * @param {string} gateway gateway
   * @param {SignUpForm} data Data informed by user
   * @returns {Promise} Resolved promise when signed up sucessfully or rejected promise on error.
   */
  finishSignUp(
    distributorId,
    uname,
    planId,
    gatewayPublicKey,
    gateway,
    data,
    hasPayment
  ) {
    if (hasPayment) {
      let ccBrand;
      const cc = data.payment.creditCard;
      const ccExp = cc.expiration.match(/(\d{2})(\d{2,4})/);
      switch (gateway) {
        case 'PAGARME':
          return axios({
            method: 'post',
            url: `https://api.pagar.me/core/v5/tokens?appId=${gatewayPublicKey}`,
            data: {
              type: 'card',
              card: {
                number: cc.number,
                holder_name: cc.name,
                exp_month: `${ccExp[1]}`,
                exp_year: `${ccExp[2]}`,
                cvv: cc.cvv,
                label: ccBrand
              }
            }
          })
            .then((hash) => {
              const cardData = {
                brand: hash.data.card.brand,
                hash: hash.data.id,
                last4Digits: cc.number.slice(-4), // We need to send only card's last 4 digits along with Moip's hash
                expiration: cc.expiration,
                name: cc.name
              };
              if (cc.owner.same) {
                cardData.cpf = data.document;
                cardData.cardAddress = {
                  city: data.address.city,
                  complement: data.address.extra,
                  country: data.address.country,
                  district: data.address.district,
                  state: data.address.state,
                  street: data.address.street,
                  streetNumber: data.address.number,
                  zipCode: data.address.zip
                };
              } else {
                const o = cc.owner;
                cardData.cpf = o.document;
                cardData.cardAddress = {
                  city: o.address.city,
                  complement: o.address.extra,
                  country: o.address.country,
                  district: o.address.district,
                  state: o.address.state,
                  street: o.address.street,
                  streetNumber: o.address.number,
                  zipCode: o.address.zip
                };
              }
              return axios({
                url: `/users/viewer/complete/${uname}`,
                method: 'put',
                data: {
                  address: {
                    city: data.address.city,
                    cityCode: data.cityCode || data.address.cityCode,
                    complement: data.address.extra,
                    country: data.address.country,
                    district: data.address.district,
                    state: data.address.state,
                    street: data.address.street,
                    streetNumber: data.address.number,
                    zipCode: data.address.zip
                  },
                  birthDate: Formatter.formatDateStringToUtcIsoAt3(
                    data.birthday
                  ),
                  card: cardData,
                  cpf: data.document,
                  distributorId,
                  name: data.name,
                  socialName: data.socialName,
                  nationality: data.nationality,
                  phone: data.phone,
                  planId
                }
              });
            })
            .then(function () {
              return Promise.resolve({
                data: {},
                code: 0,
                message: 'OK'
              });
            })
            .catch(function (error) {
              return Promise.reject(ResponseHelper.getErrorResponse(error));
            });

        case 'MOIP':
          return import('../moip-helper.js')
            .then((mod) => {
              ccBrand = mod.default.getCreditCardBrand(cc.number);
              return mod.default.generateMoipHash(
                gatewayPublicKey,
                cc.number,
                cc.cvv,
                cc.expiration
              );
            })
            .then((hash) => {
              const cardData = {
                brand: ccBrand,
                hash,
                last4Digits: cc.number.slice(-4), // We need to send only card's last 4 digits along with Moip's hash
                expiration: cc.expiration,
                name: cc.name
              };
              if (cc.owner.same) {
                cardData.cpf = data.document;
                cardData.cardAddress = {
                  city: data.address.city,
                  complement: data.address.extra,
                  country: data.address.country,
                  district: data.address.district,
                  state: data.address.state,
                  street: data.address.street,
                  streetNumber: data.address.number,
                  zipCode: data.address.zip
                };
              } else {
                const o = cc.owner;
                cardData.cpf = o.document;
                cardData.cardAddress = {
                  city: o.address.city,
                  complement: o.address.extra,
                  country: o.address.country,
                  district: o.address.district,
                  state: o.address.state,
                  street: o.address.street,
                  streetNumber: o.address.number,
                  zipCode: o.address.zip
                };
              }
              return axios({
                url: `/users/viewer/complete/${uname}`,
                method: 'put',
                data: {
                  address: {
                    city: data.address.city,
                    cityCode: data.cityCode || data.address.cityCode,
                    complement: data.address.extra,
                    country: data.address.country,
                    district: data.address.district,
                    state: data.address.state,
                    street: data.address.street,
                    streetNumber: data.address.number,
                    zipCode: data.address.zip
                  },
                  birthDate: Formatter.formatDateStringToUtcIsoAt3(
                    data.birthday
                  ),
                  card: cardData,
                  cpf: data.document,
                  distributorId,
                  name: data.name,
                  nationality: data.nationality,
                  phone: data.phone,
                  planId
                }
              });
            })
            .then((response) => {
              return Promise.resolve({
                code: 0,
                message: 'OK'
              });
            })
            .catch((error) => {
              return Promise.reject(ResponseHelper.getErrorResponse(error));
            });

        default:
          return Promise.reject(
            ResponseHelper.getErrorResponse('Gateway de pagamento invalido')
          );
      }
    } else {
      return axios({
        url: `/users/viewer/complete/${uname}`,
        method: 'put',
        data: {
          address: {
            city: data.address.city,
            cityCode: data.cityCode || data.address.cityCode,
            complement: data.address.extra,
            country: data.address.country,
            district: data.address.district,
            state: data.address.state,
            street: data.address.street,
            streetNumber: data.address.number,
            zipCode: data.address.zip
          },
          birthDate: Formatter.formatDateStringToUtcIsoAt3(data.birthday),
          card: {},
          cpf: data.document,
          distributorId,
          name: data.name,
          socialName: data.socialName,
          nationality: data.nationality,
          phone: data.phone,
          planId
        }
      }).then((response) => {
        return Promise.resolve({
          code: 0,
          message: 'OK',
          status: response.status
        });
      });
    }
  },

  /**
   * Sends (again) e-mail to user to confirm his account.
   * @param {String} distributorId
   * @param {String} uname
   * @returns {Promise}
   */
  resendConfirmationEmail(distributorId, uname) {
    return axios({
      method: 'post',
      url: `users/activate/${distributorId}/${uname}`
    })
      .then(function (response) {
        return Promise.resolve({
          data: response.data, // Returns user's e-mail
          code: 0,
          message: 'OK'
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },
  verifyUserAvailable(distributorId, uname) {
    return axios
      .$get(
        `users/viewer/available?email=${uname}&distributorId=${distributorId}`
      )
      .then(function (response) {
        return Promise.resolve({
          data: response,
          code: 0,
          message: 'OK'
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  }
});
